import clsx from 'clsx';
import { useTranslation } from 'next-i18next/pages';
import { useCallback, useEffect, useRef, useState } from 'react';

import RichTextParser from '../rich-text-parser/RichTextParser';
import RichTextViewer from './RichTextViewer';

interface ReadMoreProps {
  text: string;
  renderHtml?: boolean;
  className?: string;
  collapsedClassName?: string;
}

const ReadMore: React.FC<ReadMoreProps> = ({ text, renderHtml = false, className = 'leading-6', collapsedClassName = 'line-clamp-2' }) => {
  const { t } = useTranslation('common');
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  const checkOverflow = useCallback(() => {
    const el = contentRef.current;
    if (!el) return;
    const isNowOverflowing = el.scrollHeight > el.clientHeight;
    setIsOverflowing(prev => {
      if (prev === isNowOverflowing) return prev;
      return isNowOverflowing;
    });
  }, []);

  useEffect(() => {
    if (!contentRef.current) return;
    checkOverflow();

    const observer = new ResizeObserver(checkOverflow);
    observer.observe(contentRef.current);

    return () => observer.disconnect();
  }, [checkOverflow]);

  return (
    <div className='w-full'>
      <div
        ref={contentRef}
        className={clsx(
          'whitespace-pre-wrap transition-all duration-300',
          className,
          isCollapsed ? collapsedClassName : 'line-clamp-none',
          !isOverflowing && '[&_p]:!mb-0',
        )}
        aria-expanded={!isCollapsed}>
        {renderHtml ? <RichTextViewer text={text} className={className} /> : <RichTextParser text={text} />}
      </div>

      {(isOverflowing || !isCollapsed) && (
        <div className='mt-2 flex justify-end'>
          <button type='button' onClick={() => setIsCollapsed(prev => !prev)} className='text-main-blue text-sm underline'>
            {isCollapsed ? t('readMore') : t('readLess')}
          </button>
        </div>
      )}
    </div>
  );
};

export default ReadMore;
